import { DOC_TYPE_REQUIRED, DossierJsonldRead, Prescripteur } from '@europrocurement/l2d-domain';

import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import { Box } from '@mui/system';
import React, { useEffect, useState } from 'react';
import RequiredDocumentsList from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/RequiredDocumentsList/RequiredDocumentsList';
import RequiredDocumentUploader from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/RequiredDocumentUploader/RequiredDocumentUploader';
import {
    useDocumentSign,
    useDossierDocumentFilesApi,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { enqueueSnackbar } from 'notistack';
import {
    INIT_DOS_DOCS_CTX,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
} from '@europrocurement/l2d-modules/providers';

interface DocToSendProps {
    dossier: DossierJsonldRead;
    prescripteur: Prescripteur;
}

export const DocToSend: React.FunctionComponent<DocToSendProps> = function ({
    dossier,
    prescripteur,
}) {
    const [listItems, setListItems] = useState<Array<DisplayedDocument>>([]);
    const { dosDocs, getDossierDocumentsByCategory } = useDossierDocumentsCtx();
    const { getDosDocs } = useDossierDocumentFilesApi();
    const dossierDocumentsDispatch = useDossierDocumentsCtxDispatch();
    const { handleSignSingleDocument } = useDocumentSign();

    const handleSignSingleDocProxy = (label: string, fichierId: number, fileName?: string) => {
        const mainContact = prescripteur.contacts.find((c) => c.principal);
        const mainEmail = (mainContact?.emailsContact || []).find((email) => email?.defaut);

        if (!mainEmail) {
            enqueueSnackbar(
                'Impossible de trouver un contact avec une adresse email valide auquel rattacher ce dossier de signature électronique',
                { variant: 'error' },
            );

            return;
        }

        handleSignSingleDocument(
            dossier.id as number,
            prescripteur.id as number,
            prescripteur.raisonSociale as string,
            mainEmail?.email as string,
            label,
            fichierId,
            fileName,
        );
    };

    useEffect(() => {
        if (dossier && dossier.id) {
            getDosDocs(dossier.id as number).then((response) => {
                // Dispatch les données obtenues de l'API dans le state initial
                dossierDocumentsDispatch({
                    type: INIT_DOS_DOCS_CTX,
                    payload: response || [],
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier]);

    useEffect(() => {
        const items = [
            ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED }),
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_REQUIRED,
                additionalDocs: true,
            }),
        ].map((item) => ({
            ...item,
            movable: false,
        }));

        setListItems(items);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dosDocs]);

    const {
        handleAddFile,
        handleRemoveFile,
        handleShow,
        handleDownload,
        handleAdditionalUploads,
        submittingItemId,
    } = useDossierFile({
        typeDoc: DOC_TYPE_REQUIRED,
        dossierProps: dossier,
        displayPreviewOnAddFile: false,
    });

    const isSubmitting = submittingItemId !== null;

    return (
        <Box sx={{ display: 'grid', gap: '8px 0' }}>
            <RequiredDocumentsList
                documents={listItems}
                onUpload={handleAddFile}
                onDeletion={handleRemoveFile}
                onDownload={handleDownload}
                onShow={handleShow}
                onSign={handleSignSingleDocProxy}
                onMove={() => {}}
                onDocDeletion={() => {}}
                isSubmitting={isSubmitting}
                submittingItemId={submittingItemId}
            />
            <RequiredDocumentUploader
                uploading={submittingItemId === 'uploader'}
                disabled={isSubmitting}
                onChange={handleAdditionalUploads}
            />
        </Box>
    );
};

export default DocToSend;
